import React, {useCallback, useRef, useState} from 'react';
import Measure from 'react-measure';
import { makeStyles } from '@mui/styles';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {replaceItemInIndex, swapItemsInList} from '../../../utils/utils';
import RowLayout from '../../Layouts/RowLayout';
import SMCButton from '../../Simple/SMCButton';
import SMCIcon from '../../Simple/SMCIcon';
import draggerIcon from '../../../assets/images/dragger.svg';

function getColumnDisplayName(column) {
    if (column.name) {
        return column.name;
    }
    if (column.header && typeof column.header === 'string') {
        return column.header;
    }
    return column.id;
}

/**
 * SMCTableHeaderMenu
 * This component allows the editing of the column state, it includes a list of the columns, and allows to hide columns, or change their order
 */
function SMCTableHeaderMenu({
    columns,
    columnsState,
    setColumnsState,
    setDefaultColumnsState,
    setDefaultWidths,
    maxHeight
}) {
    const classes = useStyles();
    const rootRef = useRef();

    const [hasScrollbar, setHasScrollbar] = useState(false);

    const onDragEnd = useCallback(result => {
        // dropped outside the list
        if (!result.destination) {
            return;
        }
    
        setColumnsState(colState => swapItemsInList(colState, result.source.index, result.destination.index));
    }, [setColumnsState]);

    return (
        <div className={classes.root}>
            <DragDropContext
                onDragEnd={onDragEnd}
                onBeforeCapture={() => {
                    rootRef.current.style.width  = rootRef.current.offsetWidth  + 'px';
                    rootRef.current.style.height = rootRef.current.offsetHeight + 'px';
                }}
            >
                <Droppable ignoreContainerClipping={false} droppableId="table-menu-droppable"> 
                    {(provided, snapshot) => (
                        <div {...provided.droppableProps} ref={provided.innerRef}>
                            <Measure client scroll innerRef={rootRef} onResize={sizeInfo => setHasScrollbar(sizeInfo.client.width !== sizeInfo.scroll.width)}>
                                {({measureRef}) => (
                                    <div ref={measureRef} style={{ maxHeight: Math.min(maxHeight, 300)+'px', overflowY: 'auto' }}>
                                        {columnsState.map((colState, index) => {
                                            const column = columns.find(col => col.id === colState.id);
                                            if (!column) {
                                                return null;
                                            }
                                            const columnId = column.id;
                                            return (
                                                <Draggable key={columnId} draggableId={columnId} index={index}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            style={{
                                                                height: 28,
                                                                padding: '0 8px',
                                                                background: (index % 2 === 0) ? '#F6F7F9' : '#FFF',
                                                                // background: (colState.show) ? '#BCE3FE' : '#FFF',
                                                                ...provided.draggableProps.style
                                                            }}
                                                        >
                                                            <RowLayout height={28}>
                                                                <img src={draggerIcon} alt="drag" />
                                                                <span
                                                                    style={{
                                                                        opacity: colState.show ? undefined : '0.5',
                                                                        width: hasScrollbar ? '160px' : '180px',
                                                                        fontWeight: colState.show ? '600' : '300',
                                                                        marginRight: '20px',
                                                                        marginLeft: '4px'
                                                                    }}
                                                                >
                                                                    { getColumnDisplayName(column) }
                                                                </span>
                                                                {
                                                                    colState.show ? (
                                                                        <SMCIcon icon="eyeFull" onClick={() => setColumnsState(replaceItemInIndex(columnsState, {...colState, show: false}, index))} />
                                                                    ) : (
                                                                        <SMCIcon icon="hideEyeFull" onClick={() => setColumnsState(replaceItemInIndex(columnsState, {...colState, show: true}, index))} />
                                                                    )
                                                                }
                                                            </RowLayout>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            );
                                        })}
                                    </div>
                                )}
                            </Measure>
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
            <div className={classes.actionsRoot}>
                <SMCButton
                    variant="ghost"
                    icon="revert"
                    className={classes.actionButtons}
                    onClick={() => {
                        setDefaultColumnsState();
                        setDefaultWidths();
                    }}
                >
                    Reset columns
                </SMCButton>
            </div>
        </div>
    );
}

const useStyles = makeStyles({
    root: {
        border: '1px solid black',
        background: '#FFF',
        color: 'black',
        boxSizing: 'border-box'
    },
    actionsRoot: {
        boxSizing: 'border-box',
        height: 37,
        borderTop: '1px solid #000',
        width: '100%'
    },
    actionButtons: {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        margin: 0,
        paddingLeft: '10px'
    }
})

export default SMCTableHeaderMenu;